import { graphQLErrorsOf, networkErrorOf, serverErrorBody } from '../utils/apolloErrors';
import { ServerError } from '@apollo/client/errors';
import { useMutation } from '@apollo/client/react';
import { useCallback, useRef, useState } from 'react';
import { FileCategory } from '../graphql.d';
import AgentConfirmUploadMutation from './queries/agentConfirmUpload.mutation.graphql';
import type { AgentConfirmUploadMutation as AgentConfirmUploadMutationType } from './queries/agentConfirmUpload.mutation.graphql.d';
import AgentRequestUploadUrlMutation from './queries/agentRequestUploadUrl.mutation.graphql';
import type { AgentRequestUploadUrlMutation as AgentRequestUploadUrlMutationType } from './queries/agentRequestUploadUrl.mutation.graphql.d';

/** Max attachments per message — mirrors the backend DTO limit. */
export const MAX_ATTACHMENTS = 4;

/**
 * Per-type byte caps — mirror AgentAttachmentService. Images are capped so the
 * base64 payload stays under Anthropic's 5 MB image limit; text is sent whole,
 * so it is capped to what fits the context window.
 */
const MAX_IMAGE_BYTES = Math.floor((5 * 1024 * 1024 * 3) / 4);
const MAX_PDF_BYTES = 20 * 1024 * 1024;
const MAX_TEXT_BYTES = 256 * 1024;

/**
 * Readable types by extension. The extension wins over `file.type`: browsers
 * report an empty type for .md/.tsv on many platforms, and Windows labels .csv
 * as application/vnd.ms-excel — neither of which the agent or the storage
 * allowlist accepts.
 */
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  json: 'application/json',
};

/** File-picker `accept` list. */
export const ATTACHMENT_ACCEPT = Object.keys(MIME_BY_EXTENSION)
  .map((ext) => `.${ext}`)
  .join(',');

export interface ChatAttachment {
  /** Stable client-side id for list rendering and removal. */
  localId: string;
  /** StoredFile id once the upload is confirmed; undefined while in flight. */
  fileId?: string;
  /** The name the user sees (the original, unsanitized filename). */
  filename: string;
  size: number;
  mimeType: string;
  status: 'uploading' | 'ready' | 'error';
  error?: string;
  /** Object URL of the picked image, so the composer and the sent message can show it. */
  previewUrl?: string;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function resolveMimeType(file: File): string | undefined {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXTENSION[ext];
}

function maxBytesFor(mimeType: string): number {
  if (mimeType === 'application/pdf') return MAX_PDF_BYTES;
  if (mimeType.startsWith('image/')) return MAX_IMAGE_BYTES;
  return MAX_TEXT_BYTES;
}

/**
 * The storage upload endpoint only accepts [A-Za-z0-9._-\s()] in filenames.
 * Strip accents and replace anything else, so "Acme, Inc. — Año 2026.pdf"
 * still uploads. The chip keeps showing the original name.
 */
function toUploadFilename(name: string): string {
  const ascii = name.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const safe = ascii
    .replace(/[^a-zA-Z0-9._\-\s()]/g, '_')
    .trim()
    .slice(-255);
  return safe.replace(/^[._\s]+/, '') || 'attachment';
}

function validate(file: File, mimeType: string | undefined): string | null {
  if (!mimeType) {
    return 'Unsupported file type. Attach a PDF, image, or text file.';
  }
  const max = maxBytesFor(mimeType);
  if (file.size > max) {
    return `Too large — the limit is ${formatFileSize(max)} for this file type.`;
  }
  return null;
}

/**
 * The reason an upload step failed, as specific as the response allows — a
 * bare "400" gives the user (and whoever debugs it) nothing to act on.
 *
 * - GraphQL: a rejected request (e.g. the API doesn't know a field or enum
 *   value) arrives as an HTTP 400 whose body still carries the GraphQL error.
 * - S3: the storage error is XML with a `<Code>` and `<Message>`.
 */
function describeUploadError(error: unknown): string {
  const graphQLMessage = graphQLErrorsOf(error)[0]?.message;
  if (graphQLMessage) return graphQLMessage;
  const network = networkErrorOf(error);
  if (network) {
    const bodyMessage = serverErrorBody(network)?.errors?.[0]?.message;
    const status = ServerError.is(network) ? network.statusCode : undefined;
    if (bodyMessage) return `${bodyMessage} (${status ?? 'request rejected'})`;
    return network.message;
  }
  return error instanceof Error && error.message ? error.message : 'Upload failed.';
}

/** Read `<Code>` and `<Message>` out of an S3 error body, if there is one. */
async function describeStorageRejection(response: Response): Promise<string> {
  let body = '';
  try {
    body = await response.text();
  } catch {
    // Body unreadable (e.g. blocked by CORS) — fall back to the status alone.
  }
  const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1];
  const message = /<Message>([^<]+)<\/Message>/.exec(body)?.[1];
  const detail = [code, message].filter(Boolean).join(': ');
  return `Storage rejected the upload (${response.status}${detail ? ` ${detail}` : ''}).`;
}

let counter = 0;
const nextLocalId = (): string => `att-${Date.now()}-${counter++}`;

/**
 * Manages the files a user attaches to a chat message: client-side validation,
 * the presigned upload → PUT → confirm handshake (same flow as InvoiceFileUpload,
 * but yielding StoredFile ids rather than download URLs), and per-file state for
 * the composer chips.
 */
export function useAgentAttachments() {
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  // Mirrors the list synchronously so addFiles can count free slots without
  // reading state inside an updater (updaters must stay side-effect free).
  const countRef = useRef(0);
  const [requestUploadUrl] = useMutation<AgentRequestUploadUrlMutationType>(
    AgentRequestUploadUrlMutation,
  );
  const [confirmUpload] = useMutation<AgentConfirmUploadMutationType>(AgentConfirmUploadMutation);

  const patch = useCallback((localId: string, next: Partial<ChatAttachment>) => {
    setAttachments((current) =>
      current.map((att) => (att.localId === localId ? { ...att, ...next } : att)),
    );
  }, []);

  const upload = useCallback(
    async (localId: string, file: File, mimeType: string) => {
      try {
        const { data: uploadData } = await requestUploadUrl({
          variables: {
            input: {
              filename: toUploadFilename(file.name),
              mimeType,
              size: file.size,
              category: FileCategory.AgentAttachment,
            },
          },
        });
        const presigned = uploadData?.requestUploadUrl;
        if (!presigned) throw new Error('Could not start the upload.');

        const putResponse = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': mimeType },
        });
        if (!putResponse.ok) throw new Error(await describeStorageRejection(putResponse));
        const etag = putResponse.headers.get('etag');

        const { data: confirmData } = await confirmUpload({
          variables: {
            input: { key: presigned.key, bucket: presigned.bucket, etag: etag || undefined },
          },
        });
        const stored = confirmData?.confirmUpload;
        if (!stored) throw new Error('Could not confirm the upload.');

        patch(localId, { fileId: stored.id, status: 'ready' });
      } catch (error) {
        // Full detail for devtools; the chip gets the readable reason.
        console.error('Attachment upload failed', error);
        patch(localId, { status: 'error', error: describeUploadError(error) });
      }
    },
    [requestUploadUrl, confirmUpload, patch],
  );

  const addFiles = useCallback(
    (files: FileList | File[]): void => {
      const room = Math.max(0, MAX_ATTACHMENTS - countRef.current);
      const accepted = Array.from(files).slice(0, room);
      if (accepted.length === 0) return;

      const entries = accepted.map((file) => {
        const mimeType = resolveMimeType(file);
        const invalid = validate(file, mimeType);
        const attachment: ChatAttachment = {
          localId: nextLocalId(),
          filename: file.name,
          size: file.size,
          mimeType: mimeType ?? (file.type || 'application/octet-stream'),
          status: invalid ? 'error' : 'uploading',
          ...(invalid && { error: invalid }),
          ...(!invalid &&
            mimeType?.startsWith('image/') &&
            typeof URL.createObjectURL === 'function' && { previewUrl: URL.createObjectURL(file) }),
        };
        return { attachment, file, upload: invalid ? null : mimeType };
      });

      countRef.current += entries.length;
      setAttachments((current) => [...current, ...entries.map((entry) => entry.attachment)]);
      for (const entry of entries) {
        if (entry.upload) {
          void upload(entry.attachment.localId, entry.file, entry.upload);
        }
      }
    },
    [upload],
  );

  const remove = useCallback((localId: string) => {
    setAttachments((current) => {
      // Only a removed attachment is released; `clear` runs on send, when the message keeps the URL.
      const removed = current.find((att) => att.localId === localId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      const next = current.filter((att) => att.localId !== localId);
      countRef.current = next.length;
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    countRef.current = 0;
    setAttachments([]);
  }, []);

  const isUploading = attachments.some((att) => att.status === 'uploading');
  const failed = attachments.filter((att) => att.status === 'error');
  const isFull = attachments.length >= MAX_ATTACHMENTS;

  return {
    attachments,
    addFiles,
    remove,
    clear,
    isUploading,
    /** Attachments whose validation or upload failed — they block sending until removed. */
    failed,
    isFull,
  };
}

/** What a composer needs to drive attachments — the hook's return value. */
export type AgentAttachmentsController = ReturnType<typeof useAgentAttachments>;
